import createSagaMiddleware from "@redux-saga/core";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { crashReporter, logger } from "../../../store";
import { hallsList, hallsListLoading } from "../reducers/hallsList.reducer";
import { hallsListFilters } from "../reducers/hallsListFilters.reducer";
import { creationMode } from "../reducers/creationMode.reducer";

import {
    inspectingHall,
    inspectingHallLoading,
    inspectingHallServers,
    inspectingHallServersLoading,
    inspectingServer,
    inspectingServerConfigurationsList,
    inspectingServerConfigurationsListLoading,
    inspectingServerLoading,
    serversInfo,
    serversInfoLoading,
    submittingServerLoading,
    productionServer
} from "../reducers/inspectingHall.reducer";
import {
    subsidiaryList,
    //cashSystems,
    coreVersions,
    timezones,
    subsidiaryLoading,
    timezonesLoading,
    dynamicHalls,
    dynamicHallsLoading,
    dynamicHallInfo,
    dynamicHallInfoLoading
} from "../reducers/subsidiaries.reducer";
import {
    compareConfigurations,
    compareConfigurationsLoading
} from "../reducers/compareConfigurations.reducer";
import {
    creatingHall,
    addressSuggestions,
    submittingHallLoading,
    pickedSuggestionCount
} from "../reducers/creatingHall.reducer";
import { mapCoordinates } from "../reducers/mapCoordinates.reducer";
import rootSaga from "../sagas";
import { connectRouter, routerMiddleware } from "connected-react-router";
import { history } from "../../home/store/store";
import {
    serverLicense,
    serverLicenseState,
    serverLicenseLoading,
    licenseActivities,
    licenseActivitiesLoading,
    licenseActivitiesPage,
    serverDurationSet
} from "../reducers/licenses.reducer";
import {
    specificationUploading,
    specificationUploaded,
    specificationActivities,
    specificationActivitiesLoading,
    specificationActivitiesPage
} from "../reducers/specification.reducer";

export const hallsReducer = combineReducers({
    router: connectRouter(history),
    hallsList,
    hallsListLoading,
    subsidiaryList,
    subsidiaryLoading,
    //cashSystems,
    coreVersions,
    timezones,
    timezonesLoading,
    hallsListFilters,
    submittingServerLoading,

    creatingHall,
    mapCoordinates,
    addressSuggestions,
    pickedSuggestionCount,
    submittingHallLoading,
    creationMode,

    inspectingHall,
    inspectingHallLoading,
    inspectingHallServers,
    inspectingHallServersLoading,
    inspectingServer,
    inspectingServerLoading,
    inspectingServerConfigurationsList,
    inspectingServerConfigurationsListLoading,

    serversInfo,
    serversInfoLoading,

    compareConfigurations,
    compareConfigurationsLoading,

    serverLicense,
    serverLicenseState,
    serverLicenseLoading,
    licenseActivities,
    licenseActivitiesLoading,
    licenseActivitiesPage,
    serverDurationSet,

    specificationUploading,
    specificationUploaded,
    specificationActivitiesLoading,
    specificationActivities,
    specificationActivitiesPage,

    dynamicHalls,
    dynamicHallsLoading,
    dynamicHallInfo,
    dynamicHallInfoLoading,

    productionServer
});

const composeEnhancers = window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]
    ? window["__REDUX_DEVTOOLS_EXTENSION_COMPOSE__"]({
          name: "Halls Store"
      })
    : compose;

const sagaMiddleware = createSagaMiddleware();

export const hallsStore = createStore(
    hallsReducer,
    {},
    composeEnhancers(
        applyMiddleware(
            routerMiddleware(history),
            sagaMiddleware,
            logger,
            crashReporter
        )
    )
);

export const hallsStoreSelectors = {
    hallsList: (state = hallsStore.getState()) => {
        return state.hallsList.halls;
    },
    hallsCount: (state = hallsStore.getState()) => {
        return state.hallsList.total;
    },
    hallsListTimestamp: (state = hallsStore.getState()) => {
        return state.hallsList.timestamp;
    },
    hallsListLoading: (state = hallsStore.getState()) => {
        return state.hallsListLoading;
    },
    inspectingHall: (state = hallsStore.getState()) => {
        return state.inspectingHall;
    },
    inspectingHallLoading: (state = hallsStore.getState()) => {
        return state.inspectingHallLoading;
    },
    inspectingServer: (state = hallsStore.getState()) => {
        return state.inspectingServer;
    },
    inspectingServerLoading: (state = hallsStore.getState()) => {
        return state.inspectingServerLoading;
    },
    filters: (state = hallsStore.getState()) => {
        return state.hallsListFilters;
    },
    searchFilter: (state = hallsStore.getState()) => {
        return state.hallsListFilters.search;
    },
    subsidiaryList: (state = hallsStore.getState()) => {
        const convertToPhilippines = market => {
            const convertedMkt = { ...market };
            if (market.country.code === "PH")
                convertedMkt.client = "Philippines";
            return convertedMkt;
        };

        return state.subsidiaryList
            ? state.subsidiaryList.map(subsidiary =>
                  convertToPhilippines(subsidiary)
              )
            : [];
    },
    subsidiaryLoading: (state = hallsStore.getState()) => {
        return state.subsidiaryLoading;
    },
    filteredMarkets: (state = hallsStore.getState()) => {
        return state.hallsListFilters.country;
    },
    inspectingHallServersLoading: (state = hallsStore.getState()) => {
        return state.inspectingHallServersLoading;
    },
    filteredCountries: (state = hallsStore.getState()) => {
        return state.hallsListFilters.country;
    },
    filteredProviders: (state = hallsStore.getState()) => {
        return [`${state.hallsListFilters.provider}`];
    },
    creatingHall: (state = hallsStore.getState()) => {
        return state.creatingHall;
    },
    mapCoordinates: (state = hallsStore.getState()) => {
        return state.mapCoordinates;
    },
    addressSuggestions: (state = hallsStore.getState()) => {
        return state.addressSuggestions;
    },
    /* cashSystems: (state = hallsStore.getState()) => {
        return state.cashSystems;
    }, */
    coreVersions: (state = hallsStore.getState()) => {
        return state.coreVersions;
    },
    timezones: (state = hallsStore.getState()) => {
        return state.timezones;
    },
    timezonesLoading: (state = hallsStore.getState()) => {
        return state.timezonesLoading;
    },
    pickedSuggestionCount: (state = hallsStore.getState()) => {
        return state.pickedSuggestionCount;
    },
    select:
        (featureKey: string) =>
        (state = hallsStore.getState()) =>
            state[featureKey],
    creationMode: (state = hallsStore.getState()) => {
        return state.creationMode;
    },

    serversInfo: (state = hallsStore.getState()) => state.serversInfo,
    serversInfoLoading: (state = hallsStore.getState()) => {
        return state.serversInfoLoading;
    },
    compareConfigurations: (state = hallsStore.getState()) =>
        state.compareConfigurations,
    compareConfigurationsLoading: (state = hallsStore.getState()) =>
        state.compareConfigurationsLoading,
    submittingServerLoading: (state = hallsStore.getState()) =>
        state.submittingServerLoading,
    submittingHallLoading: (state = hallsStore.getState()) =>
        state.submittingHallLoading,

    serverLicense: (state = hallsStore.getState()) => state.serverLicense,
    serverLicenseState: (state = hallsStore.getState()) =>
        state.serverLicenseState,
    serverLicenseLoading: (state = hallsStore.getState()) =>
        state.serverLicenseLoading,
    licenseActivities: (state = hallsStore.getState()) =>
        state.licenseActivities,
    licenseActivitiesLoading: (state = hallsStore.getState()) =>
        state.licenseActivitiesLoading,
    licenseActivitiesPage: (state = hallsStore.getState()) =>
        state.licenseActivitiesPage,
    serverDurationSet: (state = hallsStore.getState()) =>
        state.serverDurationSet,
    specificationUploading: (state = hallsStore.getState()) =>
        state.specificationUploading,
    specificationUploaded: (state = hallsStore.getState()) =>
        state.specificationUploaded,
    specificationActivitiesLoading: (state = hallsStore.getState()) =>
        state.specificationActivitiesLoading,
    specificationActivities: (state = hallsStore.getState()) =>
        state.specificationActivities,
    specificationActivitiesPage: (state = hallsStore.getState()) =>
        state.specificationActivitiesPage,
    dynamicHalls: (state = hallsStore.getState()) => {
        return state.dynamicHalls;
    },
    dynamicHallsLoading: (state = hallsStore.getState()) => {
        return state.dynamicHallsLoading;
    },
    dynamicHallInfo: (state = hallsStore.getState()) => {
        return state.dynamicHallInfo;
    },
    dynamicHallInfoLoading: (state = hallsStore.getState()) => {
        return state.dynamicHallInfoLoading;
    },
    productionServer: (state = hallsStore.getState()) => {
        return state.productionServer;
    }
};

sagaMiddleware.run(rootSaga);
